import { Logger } from '../../../../../FrontendBundle/Resources/js/ofcs';
import Cell from './cell';
import Composite from './composite';
import Dimension from './dimension';

/**
 * Represents the matrix of a grid
 */
export default class Matrix {
    /**
     * @param {number} rows
     * @param {number} columns
     */
    constructor(rows, columns) {
        this.rows = rows;
        this.columns = columns;
        this.cells = [];

        this.cells = new Array(rows)
            .fill(null)
            .map(() => new Array(columns).fill(null));
    }

    /**
     * Returns the content for a given row and cell
     * in the grid's matrix
     *
     * @param {Cell} cell
     * @return {*}
     */
    getContent(cell) {
        if (!this.cells[cell.getRow()]) return undefined;
        return this.cells[cell.getRow()][cell.getColumn()];
    }

    /**
     * Return the largest free area that matches the minimum dimensions
     *
     * @param {Dimension} [minDimensions]
     * @return {Composite|null}
     */
    getLargestFreeArea(minDimensions = new Dimension(1, 1)) {
        let largestComposite = null;

        // Iterate over every possible dimension size starting with the largest
        for (let currentRow = this.rows; currentRow >= minDimensions.getRows(); currentRow -= 1) {
            for (let currentColumn = this.columns; currentColumn >= minDimensions.getColumns(); currentColumn -= 1) {
                const dimensions = new Dimension(currentRow, currentColumn);

                // Iterate over every possible start position for the current area
                for (let startRow = 0; startRow <= this.rows - currentRow; startRow += 1) {
                    for (let startColumn = 0; startColumn <= this.columns - currentColumn; startColumn += 1) {
                        const startCell = new Cell(startRow, startColumn);
                        const composite = new Composite(startCell, dimensions);

                        // Check if the area is applicable and better then the current one
                        if (this.isFree(composite)) {
                            largestComposite = this._getPreferredComposite(composite, largestComposite);
                        }
                    }
                }
            }
        }

        return largestComposite;
    }

    /**
     * @param {Composite} composite
     * @return {number}
     * @private
     */
    _getRatioScore(composite) {
        const ratio = composite.getRatio();
        return ratio > 1 ? 1 / ratio : ratio;
    }

    /**
     * @param {Composite} compositeA
     * @param {Composite} compositeB
     * @return {Composite}
     * @private
     */
    _getPreferredComposite(compositeA, compositeB) {
        if (!compositeB) return compositeA;

        if (compositeA.getArea() > compositeB.getArea()) return compositeA;
        if (compositeA.getArea() < compositeB.getArea()) return compositeB;

        if (this._getRatioScore(compositeA) > this._getRatioScore(compositeB)) return compositeA;

        return compositeB;
    }

    /**
     * Returns the first free cell when scanning
     * the grid matrix row by row from left to right
     *
     * @param {Dimension} dimension
     * @return {Cell|null} the top most left cell in the grid for the requested dimension iff free,
     *          else null
     */
    getFreeCell(dimension) {
        for (let row = 0; row < this.cells.length; row += 1) {
            for (let column = 0; column < this.cells[row].length; column += 1) {
                if (this.isAreaFreeAtCell(row, column, dimension)) {
                    return new Cell(row, column);
                }
            }
        }

        return null;
    }

    /**
     * Check if the given dimension starting at the given cell is free
     *
     * @param {number} cellRow
     * @param {number} cellColumn
     * @param {Dimension} dimension
     * @return {boolean}
     */
    isAreaFreeAtCell(cellRow, cellColumn, dimension) {
        const lastRow = cellRow + dimension.getRows();
        const lastColumn = cellColumn + dimension.getColumns();

        for (let row = cellRow; row < lastRow; row += 1) {
            for (let column = cellColumn; column < lastColumn; column += 1) {
                if (!this.isCellFree(row, column)) {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * Checks if the given cell is free
     *
     * @param {number} row
     * @param {number} column
     * @return {boolean}
     */
    isCellFree(row, column) {
        return row < this.cells.length // checks if vertical dimension fits into remaining row(s)
            && column < this.cells[row].length // checks if horizontal dimension fits into remaining column(s)
            && this.cells[row][column] === null; // checks if respective cells are not already occupied
    }

    /**
     * Based on the given input SETS the corresponding cells
     * to the given content
     *
     * @param {Cell|Composite} input
     * @param {*} content
     */
    mark(input, content = true) {
        const composite = this._toComposite(input);
        const cellRow = composite.getCell().getRow();
        const cellColumn = composite.getCell().getColumn();

        for (let row = 0; row < composite.getDimension().getRows(); row += 1) {
            for (let column = 0; column < composite.getDimension().getColumns(); column += 1) {
                this.cells[cellRow + row][cellColumn + column] = content;
            }
        }
    }

    /**
     * Based on the given input RESETS the corresponding cells
     * to be marked as 'free'
     *
     * @param {Cell|Composite} input
     */
    free(input) {
        const composite = this._toComposite(input);

        for (let row = 0; row < composite.getDimension().getRows(); row += 1) {
            for (let column = 0; column < composite.getDimension().getColumns(); column += 1) {
                this.cells[composite.getCell().getRow() + row][composite.getCell().getColumn() + column] = null;
            }
        }
    }

    /**
     * Based on the given content RESETS the matching cell
     * to be marked as 'free'. For composites spanning various
     * rows and columns all corresponding cells will be freed.
     *
     * @param {Composite} content
     */
    freeByContent(content) {
        for (let row = 0; row < this.cells.length; row += 1) {
            for (let column = 0; column < this.cells[row].length; column += 1) {
                if (this.cells[row][column] === content) {
                    this.cells[row][column] = null;
                }
            }
        }
    }

    /**
     * Unifies various inputs (cells, ranges, composites)
     * to a composite format.
     *
     * @param {Cell|Composite} input
     * @return {Composite}
     * @private
     */
    _toComposite(input) {
        if (input instanceof Composite) {
            return input;
        }

        if (input instanceof Cell) {
            return new Composite(input, new Dimension(1, 1));
        }

        throw new Error('Unknown target type');
    }

    /**
     * Checks for various inputs (cell, range, composite)
     * if all contained cells are currently free in the grid
     * matrix
     *
     * @param {Cell|Composite} input
     * @param {Composite} testContent
     * @return {boolean}
     */
    isFree(input, testContent = false) {
        const composite = this._toComposite(input);

        if (composite.getDimensionCell().getRow() > this.rows
            || composite.getDimensionCell().getColumn() > this.columns) {
            return false;
        }

        if (composite.getDimension().getRows() <= 1 && composite.getDimension().getColumns() <= 1) {
            const content = this.getContent(composite.getCell());
            return content === null || (testContent && content === testContent);
        }

        for (let row = 0; row < composite.getDimension().getRows(); row += 1) {
            for (let column = 0; column < composite.getDimension().getColumns(); column += 1) {
                const content = this.getContent(
                    new Cell(
                        composite.getCell().getRow() + row,
                        composite.getCell().getColumn() + column,
                    ),
                );

                if (content !== null && (!testContent || content !== testContent)) {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * Debugging helper function
     * Prints current matrix status to console with '#' representing occupied cells and '.' representing free cells
     */
    dump() {
        const seperator = '-'.repeat(this.columns);
        const s = `+${seperator}+
${this.cells.map(row => this._dumpRow(row)).join('\n')}
+${seperator}+`;

        Logger.info(s);
    }

    _dumpRow(row) {
        return `|${row.map(column => (column ? '#' : '.')).join('')}|`;
    }
}
